import { FC, useEffect, useState } from "react";
import useInbox from "hooks/useInbox.ts";
import Icon from "components/Elements/Icon/Icon";
import { useContent } from "contexts/contentContext/contentContext";
import GetTransformedDate from "utils/dateViewTransformer";
import bytesToDisplayHelper from "utils/bytesToDisplayHelper";
import Button from "components/Elements/Button/Button";
import ShareComponent from "components/Reusable/SideBar/Share/ShareSideComponent";
import buildGridFunc from "components/Reusable/Grid/CoolGrid/gridCalculation";
import GridItemContainer from "components/Reusable/Grid/CoolGrid/GridItemContainer";
import Image from "components/Elements/Image/Image";
import { useLightBox } from "contexts/lightBoxContext/lightBoxContext";
import LoadingContainerElement from "components/Elements/LoadingContainerElement/LoadingContainerElement";
import MailDownloadLink from "../../Reusable/MailDownloadLink";
//@ts-ignore
import config from "config.ts";
import { useComments } from "contexts/commentsContext/commentsContext";
import { CommentDataI, MailDataI, RelevantFileModelI } from "models/models";
import useMediaQueries from "hooks/useMediaQueries.js";
import {
  ReactGetEmbedFormLeadDetailsApiRequest,
  type ReactGetEmbedFormLeadDetailsApiRequestResponseT,
} from "api/MailsController.ts";

interface MailInfoI {
  data: MailDataI;
}

interface TabBarContentI {
  selectedTab: string;
}
interface CommentsTabI {
  commentsQuantity: number;
  mailId: string;
}

const headerIcons: {
  [key: string]: string;
} = {
  Image: "image",
  Audio: "music-note",
  Video: "play",
  Zip: "zip",
  pdf: "pdf",
  Doc: "file",
};

const MailInfo: FC<MailInfoI> = (props) => {
  const { data } = props;
  const { getTextByKey, getPath, getDirection } = useContent();
  const { setLightBox } = useLightBox();
  const { mobileCondition } = useMediaQueries();
  const {
    fetchSideInboxFiles,
    getSideInboxFiles,
    getSelectedMail,
    getIsFetchingSideInboxFiles,
    getHoveredMail,
  } = useInbox();
  const sideInboxFiles = getSideInboxFiles()?.[props.data?.id];

  const filesArray = sideInboxFiles ? Object.keys(sideInboxFiles) : [];
  const { renderList, containerHeight } = buildGridFunc({
    list: filesArray,
    obj: sideInboxFiles,
    width: 360,
    maxHeight: 100,
    margin: 5,
    rtl:
      window.location.href.includes("/he/") ||
      window.location.href.includes("/ar/")
        ? true
        : false,
    hideAds: true,
  });
  const [formFields, setFormFields] = useState<{
    [key: string]: ReactGetEmbedFormLeadDetailsApiRequestResponseT;
  }>({});

  useEffect(() => {
    if (
      !getHoveredMail()?.id &&
      !getSelectedMail()?.security?.isPasswordProtected
    ) {
      fetchSideInboxFiles({
        encryptedMailId: getSelectedMail()?.id!,
        numberOfFiles: 9,
      });
    }
  }, [getSelectedMail()?.id]);

  useEffect(() => {
    if (
      getHoveredMail()?.id &&
      !getHoveredMail()?.security?.isPasswordProtected
    ) {
      fetchSideInboxFiles({
        encryptedMailId: getHoveredMail()?.id!,
        numberOfFiles: 9,
      });
    }
  }, [getHoveredMail()?.id]);

  const getFormFields = async (mailId: string) => {
    const response = await ReactGetEmbedFormLeadDetailsApiRequest(mailId);

    if (response.status === "success") {
      setFormFields((prev) => ({
        ...prev,
        [mailId]: response.data!,
      }));
    }
  };

  useEffect(() => {
    const mailId = data?.id;
    if (data?.isJForm && mailId && !formFields[mailId]) {
      getFormFields(mailId);
    }
  }, [data?.isJForm, data?.id, formFields]);

  const handleShowLightBox = (data: any) => {
    setLightBox(data?.Type, data?.id, data);
  };

  const MailData = () => (
    <ul className="mail-split-info-list">
      <li>
        <span>{getTextByKey("split-info-from")}</span>
        <span>
          {(data?.SenderInfo?.fullName + "").trim() ||
            data?.SenderInfo?.senderEmail}
        </span>
      </li>
      <li>
        <span>{getTextByKey("split-info-recipients")}</span>
        <span>{data.recipientsList?.join(", ")}</span>
      </li>
      <li>
        <span>{getTextByKey("split-info-creation-date")}</span>
        <span>
          <GetTransformedDate date={data.creationDate} />
        </span>
      </li>
      <li>
        <span>{getTextByKey("split-info-expiration-date")}</span>
        <span>
          <GetTransformedDate date={data.expirationDate} />
        </span>
      </li>
      <li>
        <span>{getTextByKey("split-info-downloads")}</span>
        <span className={data?.downloadsQuantity ? "download-link" : ""}>
          <MailDownloadLink mailData={data} withText={false} />
        </span>
      </li>
      <li>
        <span>{getTextByKey("split-info-files")}</span>
        <span>{data.filesQuantity}</span>
      </li>
      <li>
        <span>{getTextByKey("split-info-size")}</span>
        <span>
          {Object.values(bytesToDisplayHelper(data.filesSize)).join("")}
        </span>
      </li>
    </ul>
  );

  const FormData = () => (
    <ul className="mail-split-info-list">
      <li>
        <span>{getTextByKey("split-info-email")}</span>
        <span>{data?.recipientsList}</span>
      </li>
      <li>
        <span>{getTextByKey("split-info-form")}</span>
        <span>{data.formTitle}</span>
      </li>
      <li>
        <span>{getTextByKey("split-info-transaction")}</span>
        <span>{data.transactionId}</span>
      </li>
      {!!data.id &&
        formFields[data.id]?.map(({ field, value, type }, index) => {
          let fieldvalue = value;
          if (type === "recipientDropdown") {
            const recipientListParce = JSON.parse(value);
            fieldvalue = recipientListParce
              .map((recipient: { name: string }) => recipient.name)
              .join(", ");
          }
          return (
            <li key={field + index}>
              <span>{field}:</span>
              <span>{fieldvalue}</span>
            </li>
          );
        })}
      <li>
        <span>{getTextByKey("split-info-downloads")}</span>
        <span className={data?.downloadsQuantity ? "download-link" : ""}>
          <MailDownloadLink mailData={data} withText={false} />
        </span>
      </li>
      <li>
        <span>{getTextByKey("split-info-files")}</span>
        <span>{data.filesQuantity}</span>
      </li>
      <li>
        <span>{getTextByKey("split-info-size")}</span>
        <span>
          {Object.values(bytesToDisplayHelper(data.filesSize)).join("")}
        </span>
      </li>
    </ul>
  );

  const getItemIcon = (fileData: any) => {
    return fileData.Type === "Doc"
      ? headerIcons[fileData.Extention] || headerIcons.Doc
      : headerIcons[fileData.Type] || "file";
  };

  const baseUrl = data?.bucketName
    ? data.bucketName.includes("jumbomail1")
      ? window.location.origin
      : data.bucketName.replace("files.", "")
    : config.baseurl;

  return !!data ? (
    <div className="mail-split-info">
      <div className="title">
        <Icon name="folder-solid" xxxl lightgray iconClass="folder-icon" />
        <h2>
          {data.isJForm
            ? `${data?.formTitle} (${data?.deliveryNumber})`
            : data.subject || getTextByKey("no-subject")}
        </h2>
      </div>
      {!data.isJForm ? <MailData /> : <FormData />}
      {data?.mailStatus !== "removed" &&
        data?.mailStatus !== "pendingRemoved" && (
          <>
            {data.security?.isPasswordProtected ? (
              <div className="password-protected-info">
                <Icon name="lock-closed-solid" gray />
                {getTextByKey("split-info-password")}
              </div>
            ) : !getIsFetchingSideInboxFiles() ? (
              <div className="grid-preview" style={{ height: containerHeight }}>
                {renderList
                  // .filter(({ Type }) => Type === "Image")
                  .map((item, i) => (
                    <GridItemContainer
                      item={item.folder ? item.folder + 1 : item}
                      key={item.id}
                      width={100}
                      alwaysShow
                    >
                      {item.ThumbSrc ? (
                        <Image
                          src={item.ThumbSrc}
                          className="thumb-preview"
                          onClick={() => handleShowLightBox(item)}
                        />
                      ) : (
                        <>
                          <div className="inbox-item-icon">
                            <Icon name={getItemIcon(item)} gray xxl />
                            {getItemIcon(item) === "file" && (
                              <span
                                style={
                                  mobileCondition
                                    ? {
                                        transform: `translate(${
                                          getDirection() === "rtl"
                                            ? "25px"
                                            : "-20px"
                                        })`,
                                      }
                                    : undefined
                                }
                                className={
                                  data.mailStatus === "removed"
                                    ? "removed-item"
                                    : ""
                                }
                              >
                                {item.Extention}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </GridItemContainer>
                  ))}
              </div>
            ) : (
              <div className="loader-overriding">
                <LoadingContainerElement />
              </div>
            )}
          </>
        )}
      {data?.mailStatus === "removed" ||
      data?.mailStatus === "pendingRemoved" ? (
        <div className="expired-files-message-wrapper">
          <Image name="expired-mail" ext="svg" />
          <p>{getTextByKey("split-files-expired")}</p>
        </div>
      ) : (
        <div className="inbox-split-see-all">
          <a
            className="mail-subject"
            //@ts-ignore
            href={`${baseUrl}${getPath()}gallery/${data.id}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Button img={undefined} title="See all" buttonClass="" blank>
              {getTextByKey("inbox-split-see-all")}
            </Button>
          </a>
        </div>
      )}
    </div>
  ) : (
    <></>
  );
};

const CommentsTab: FC<CommentsTabI> = ({ commentsQuantity, mailId }) => {
  const { fetchCommentsData, getAllCommentsData } = useComments();
  const commentedFiles = getAllCommentsData()
    ? Array.from(
        new Set(
          Object.keys(getAllCommentsData())
            .filter((key) => !!getAllCommentsData()[key].RelevantFileModel)
            .map((key) => getAllCommentsData()[key].RelevantFileModel.Name)
        )
      )
    : [];
  const particularComments = getAllCommentsData()
    ? Object.keys(getAllCommentsData()).filter(
        (key) =>
          !!getAllCommentsData()[key].RelevantFileModel &&
          getAllCommentsData()[key].Parent === null
      )
    : [];
  const generalComments = getAllCommentsData()
    ? Object.keys(getAllCommentsData()).filter(
        (key) =>
          !getAllCommentsData()[key].RelevantFileModel &&
          getAllCommentsData()[key].Parent === null
      )
    : [];
  const childrenComments = getAllCommentsData()
    ? Object.keys(getAllCommentsData()).filter(
        (key) => getAllCommentsData()[key].Parent !== null
      )
    : [];

  useEffect(() => {
    if (commentsQuantity && mailId) {
      fetchCommentsData(mailId);
    }
  }, [commentsQuantity, mailId]);

  const CommentList: FC<{ comments: string[] }> = ({ comments }) => (
    <ul className="comment-list">
      {comments.map((comment) => (
        <li key={getAllCommentsData()[comment].Id}>
          <div className="comment-header">
            <Image
              src={getAllCommentsData()[comment].UserAvatar}
              alt="commentator profile"
            />
            <p>{getAllCommentsData()[comment].UserName}</p>
          </div>
          <p>{getAllCommentsData()[comment].Content}</p>
          <div className="comment-footer">
            <p>
              <GetTransformedDate
                date={getAllCommentsData()[comment].Created}
                showTime
              />
            </p>
          </div>
        </li>
      ))}
    </ul>
  );

  return !!getAllCommentsData() ? (
    <div>
      <CommentList comments={generalComments} />
      {commentedFiles.map((fileName) => {
        const fileData: RelevantFileModelI = (
          Object.values(getAllCommentsData() as CommentDataI[]).find(
            (item) => item?.RelevantFileModel?.Name === fileName
          ) as CommentDataI
        )?.RelevantFileModel;

        return (
          <>
            <div>
              <Image src={fileData.ThumbSrc} />
              <span>{fileData.Name}</span>
            </div>
            <CommentList
              comments={particularComments.filter(
                (key) =>
                  getAllCommentsData()[key]?.RelevantFileModel.Name === fileName
              )}
            />
          </>
        );
      })}

      {/* <CommentList comments={particularComments} /> */}
    </div>
  ) : (
    <></>
  );
};

const TabBarContent: FC<TabBarContentI> = (props) => {
  const { selectedTab } = props;
  const { getSelectedMail, getHoveredMail } = useInbox();

  switch (selectedTab) {
    case "tab-info":
      return (
        <MailInfo data={getHoveredMail() || (getSelectedMail() as MailDataI)} />
      );
    case "tab-comments":
      return (
        <CommentsTab
          commentsQuantity={getSelectedMail()?.commentsQuantity!}
          mailId={getSelectedMail()?.id!}
        />
      );
    case "tab-share":
      return (
        <div className="tab-bar-share-component-wrapper">
          <ShareComponent
            itemId={undefined}
            folderId={undefined}
            children={undefined}
            collection={undefined}
            email={undefined}
            lightBox={undefined}
            mailData={
              getSelectedMail() as unknown as
                | {
                    id: null;
                  }
                | undefined
            }
          />
        </div>
      );
    default:
      return <></>;
  }
};
export default TabBarContent;
